/**
 *
 * AcademicYearForm
 *
 */
import { Input } from 'core/components/Input';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { AcademicYear } from 'core/utils/types/modules/org';
import { useBranchSetupSlice } from 'features/BranchSetup/slice';
import { RootState } from 'controller/types';
import { useDispatch, useSelector } from 'react-redux';
import React, { memo, useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { getCurrentDate } from 'core/utils/constants/DateTime';
import { useParams } from 'react-router-dom';
import { error } from 'core/components/Notify';
import { SubmitButton } from 'core/components/SetupComponents/SubmitButton';
import DateTimePicker from 'core/components/DateTimePicker';
import moment from 'moment';
import dt from 'date-and-time';
import { DropDown } from 'libs/DropDown';
import { isValidInput } from 'core/utils/constants';

interface Props {
  values?: AcademicYear | any;
  action?: string;
  onNext?: () => void;
}

export const AcademicYearForm = memo((props: Props) => {
  const authData = useAuth();
  const dispatch = useDispatch();
  const { actions } = useBranchSetupSlice();
  const params: any = useParams();
  const state = useSelector((state: RootState) => state.branchSetup);
  
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [academicYearStatus, setAcademicYearStatus] = useState<string>('');
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const nextYear = dt.addYears(new Date(), 1);

  const [values, setValues] = useState<AcademicYear>(
    props.values
      ? (props.values as AcademicYear)
      : ({
        NAME: '',
        START_DATE: getCurrentDate(new Date()),
        END_DATE: getCurrentDate(new Date(nextYear)),
        ACADEMIC_YEAR_STATUS: '',
      } as AcademicYear | any),
  );

  useEffect(() => {
    if (props.values?.ACADEMIC_YEAR_ID) {
      setValues(props.values as AcademicYear | any);
      setStartDate(new Date(props.values.START_DATE) as any);
      setEndDate(new Date(props.values.END_DATE) as any);
      setAcademicYearStatus(props?.values?.ACADEMIC_YEAR_STATUS);
    } else {
      setAcademicYearStatus('PREVIOUS');
      setStartDate(new Date());
      setEndDate(new Date(nextYear));
    }
  }, [props?.values?.ACADEMIC_YEAR_ID]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    setIsDirty(true); // Mark the form as dirty when any input changes
  };

  useEffect(() => {
    if (state?.navigate) {
      props?.onNext && props?.onNext();
      dispatch(actions.setNavigate(false));
    }
  }, [state?.navigate]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (startDate && endDate && moment(startDate).isAfter(moment(endDate))) {
      error('End Date must be greater than Start Date', true);
      return;
    }
    dispatch(
      actions.createBranchSetupStart({
        URL: params?.url || 'academicYear',
        data: {
          ...values,
          BRANCH_ID: authData?.BRANCH_ID,
          START_DATE: getCurrentDate(startDate, true) as string,
          END_DATE: getCurrentDate(endDate, true) as string,
          ACADEMIC_YEAR_STATUS: academicYearStatus,
        } as AcademicYear,
        action: props.action || 'save ',
        message: props.action ? 'Updated Successfully!' : 'Created Successfully!',
      }),
    );
    setIsDirty(false); // Reset isDirty after successful submission
  };

  return (
    <Div onSubmit={onSubmit}>
      <div className="grid grid-cols-2 gap-[2vw]">
        <Input
          name="NAME"
          placeholder="Enter Academic Year Name"
          onChange={handleInputChange}
          value={values.NAME}
          label={'Name'}
          required
          maxLength={100}
        />
        {!!academicYearStatus && (
          <DropDown
            label="Academic Year Status"
            getOptionValue={option => option.ID}
            getOptionLabel={option => option.NAME}
            options={[
              { ID: 'PREVIOUS', NAME: 'PREVIOUS', VALUE: 'PREVIOUS' },
              { ID: 'CURRENT', NAME: 'CURRENT', VALUE: 'CURRENT' },
              { ID: 'UPCOMING', NAME: 'UPCOMING', VALUE: 'UPCOMING' },
            ]}
            onChange={item => {
              setAcademicYearStatus(item.ID);
              setIsDirty(true); // Mark as dirty when status changes
            }}
            defaultValue={[
              { ID: 'PREVIOUS', NAME: 'PREVIOUS', VALUE: 'PREVIOUS' },
              { ID: 'CURRENT', NAME: 'CURRENT', VALUE: 'CURRENT' },
              { ID: 'UPCOMING', NAME: 'UPCOMING', VALUE: 'UPCOMING' },
            ].find(item => item?.ID === academicYearStatus)}
            placeholder="Select Academic Year Status"
            required
          />
        )}
        <DateTimePicker
          minMaxNotRequired={true}
          onChange={item => {
            setStartDate(item);
            setIsDirty(true); // Mark as dirty when start date changes
          }}
          required={true}
          value={startDate || new Date()}
          error={!!startDate && !!endDate && moment(startDate).isAfter(moment(endDate))}
          isedit={props?.action === 'update'}
          label={'Start Date'}
          name="START_DATE"
          message={'Start date should be less than the end date'}
          placeholder="Enter Start Date"
          className="absolute mt-2"
          maxDate={dt.addYears(new Date(), 100)}
          minDate={new Date(1900, 0, 1)}
        />
        <DateTimePicker
          minMaxNotRequired={true}
          onChange={item => {
            setEndDate(item);
            setIsDirty(true); // Mark as dirty when end date changes
          }}
          required={true}
          error={!!startDate && !!endDate && moment(endDate).isBefore(moment(startDate))}
          message={'End date should be greater than the start date'}
          value={endDate || new Date()}
          isedit={props?.action === 'update'}
          label={'End Date'}
          name="END_DATE"
          placeholder="Enter End Date"
          className="absolute mt-2"
          maxDate={!!startDate ? dt.addYears(new Date(startDate), 1.5) : dt.addYears(new Date(), 100)}
          minDate={!!startDate ? startDate : new Date(1900, 0, 1)}
        />
      </div>
      <div className='text-center mt-3'>
        <SubmitButton
          inputValidate={
            values &&
            (!startDate ||
              !endDate ||
              (values.NAME !== undefined && !values.NAME.length || !isValidInput(values.NAME)))
          }
          isDirty={isDirty}
        />
      </div>
    </Div>
  );
});

const Div = styled.form``;